import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import moment from 'moment';
import DataGrid from '../../../components/DataGrid';
import Forms from '../../../components/Forms';
import API from '../../../utils/API';
import {API_ENDPOINTS} from '../../../apiConfig';
import {useModal} from '../../../contexts/ModalContext';
import {ReactComponent as DeleteIcon} from '../../../assets/images/delete.svg';
import {ReactComponent as EditIcon} from '../../../assets/images/edit.svg';
import DiscographyItemModal from './DiscographyItemModal';

const DiscographyItems = () => {
    const {t} = useTranslation();
    const {openModal, confirm} = useModal();

    const [items, setItems] = useState([]);
    const [albums, setAlbums] = useState([]);
    const [q, setQ] = useState('');
    const [albumFilter, setAlbumFilter] = useState(null); // { value, label } | null

    const fetchItems = useCallback(async () => {
        try {
            const res = await API.get(API_ENDPOINTS.discographyItems.list);
            setItems(res.data || []);
        } catch (e) {
            console.error('Failed to fetch discography items:', e);
        }
    }, []);

    const fetchAlbums = useCallback(async () => {
        try {
            const res = await API.get(API_ENDPOINTS.discographyAlbums.list);
            setAlbums(res.data || []);
        } catch (e) {
            console.error('Failed to fetch albums for items:', e);
        }
    }, []);

    useEffect(() => { fetchItems(); }, [fetchItems]);
    useEffect(() => { fetchAlbums(); }, [fetchAlbums]);

    const albumById = useMemo(() => {
        const m = new Map();
        (albums || []).forEach(a => m.set(String(a._id), a));
        return m;
    }, [albums]);

    const openItemModal = (item) => {
        openModal(
            DiscographyItemModal,
            {
                item,
                albums,
                onItemsUpdate: fetchItems
            },
            { title: item ? t('discography.editItem') : t('discography.addItem') }
        );
    };

    const confirmDelete = async (item) => {
        const ok = await confirm(t('discography.confirmDeletion', {name: item.name}));
        if (ok === true) {
            try {
                await API.delete(API_ENDPOINTS.discographyItems.delete(item._id));
                setItems(prev => prev.filter(i => i._id !== item._id));
            } catch (e) {
                console.error('Failed to delete item:', e);
            }
        }
    };

    const rows = useMemo(() => {
        const qlc = q.trim().toLowerCase();
        const albumIdFilter = albumFilter?.value ? String(albumFilter.value) : null;

        return (items || []).map(it => {
            const albumObj = typeof it.album === 'object' && it.album !== null ? it.album : albumById.get(String(it.album));
            return {
                ...it,
                _albumId: albumObj?._id || it.album || null,
                _albumName: albumObj?.name || ''
            };
        }).filter(it => {
            if (albumIdFilter && String(it._albumId) !== albumIdFilter) return false;
            if (qlc) {
                const hay = `${it.name || ''} ${it.host || ''} ${it.music || ''} ${it.czechLyrics || ''}`.toLowerCase();
                if (!hay.includes(qlc)) return false;
            }
            return true;
        });
    }, [items, q, albumFilter, albumById]);

    const columns = [
        {
            key: 'name',
            name: t('discography.itemName'),
            width: '28%',
            renderCell: ({row}) => (
                <label className="flex justify-between">
                    <div className="font-semibold">{row.name}</div>
                    <div className="flex">
                        <DeleteIcon className="w-4 cursor-pointer fill-red-700 m-2" onClick={() => confirmDelete(row)}/>
                        <EditIcon className="w-4 cursor-pointer text-blue-800 h-full" onClick={() => openItemModal(row)}/>
                    </div>
                </label>
            )
        },
        {
            key: '_albumName',
            name: t('discography.album'),
            width: '18%',
            renderCell: ({row}) => row._albumName || '-'
        },
        {
            key: 'host',
            name: t('discography.host'),
            width: '14%',
            renderCell: ({row}) => row.host || '-'
        },
        {
            key: 'music',
            name: t('discography.music'),
            width: '14%',
            renderCell: ({row}) => row.music || '-'
        },
        {
            key: 'czechLyrics',
            name: t('discography.czechLyrics'),
            width: '26%',
            renderCell: ({row}) => {
                const s = (row.czechLyrics || '').replace(/\s+/g, ' ').trim();
                return s.length > 150 ? `${s.slice(0, 150)}…` : (s || '-');
            }
        },
        {
            key: 'createdAt',
            name: t('createdAt'),
            width: 140,
            renderCell: ({row}) => row.createdAt ? moment(row.createdAt).format('DD.MM.YYYY HH:mm') : '-'
        }
    ].map(c => ({...c, resizable: true}));

    const albumOptions = useMemo(() => (albums || []).map(a => ({ value: a._id, label: a.name })), [albums]);

    return (
        <div>
            <div className="flex items-center justify-between mb-4">
                <Forms.Button label={t('discography.addItem')} onClick={() => openItemModal()}/>
                <div className="grid grid-cols-2 gap-3 items-end">
                    <Forms.Select
                        label={t('discography.album')}
                        value={albumFilter}
                        options={albumOptions}
                        onChange={setAlbumFilter}
                        isClearable
                    />
                    <Forms.Text
                        label={t('search')}
                        placeholder={t('discography.searchByName')}
                        value={q}
                        onChange={setQ}
                    />
                </div>
            </div>

            {(q || albumFilter) && (
                <div className="flex justify-end mb-3">
                    <Forms.Button
                        variant="secondary"
                        label={t('clearFilters')}
                        onClick={() => { setQ(''); setAlbumFilter(null); }}
                    />
                </div>
            )}

            <DataGrid rows={rows} columns={columns}/>
        </div>
    );
};

export default DiscographyItems;
